import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@app/hooks';
import type { ClaimDirection, ClaimType } from '@entities/legal/types';
import { useAgentPermission } from '@entities/user';
import { useListClaimsQuery } from '@shared/api';
import { downloadCsv } from '@shared/lib/downloadCsv';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import {
  paginationWindow,
  RegistryBar,
  RegistryExportButton,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  RegistrySearchBox,
  RegistryToolbar,
} from '@shared/ui/RegistryShell';
import { ClaimsTable } from './ClaimsTable';
import {
  DIRECTION_OPTIONS,
  PAGE_SIZE,
  SORT_OPTIONS,
  STAGE_OPTIONS,
  TYPE_OPTIONS,
} from './constants';
import { CreateClaimModal } from './CreateClaimModal';
import { ClaimsBarRight, ClaimsResetButton, Note } from './styles';
import type { Mode } from './types';

export function ClaimsTab() {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [typeFilter, setTypeFilter] = useState<'' | ClaimType>('');
  const [directionFilter, setDirectionFilter] = useState<'' | ClaimDirection>('');
  const [stageFilter, setStageFilter] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [search, typeFilter, directionFilter, stageFilter, sort, order]);

  const queryArg = useMemo(
    () => ({
      claim_type: typeFilter || undefined,
      direction: directionFilter || undefined,
      stage: stageFilter || undefined,
      search: search || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, typeFilter, directionFilter, stageFilter, sort, order, page],
  );

  const { data, isLoading, isFetching } = useListClaimsQuery(queryArg);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [isExporting, setIsExporting] = useState(false);
  async function onExport() {
    setIsExporting(true);
    try {
      await downloadCsv(
        '/agents/statut/claims/export.csv',
        {
          claim_type: typeFilter,
          direction: directionFilter,
          stage: stageFilter,
          search,
          sort,
          order,
        },
        accessToken,
        'claims.csv',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('upload');

  function toggleSort(field: string) {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('desc');
    }
  }

  const pages = paginationWindow(page, totalPages);

  const emptyMessage = search || typeFilter || stageFilter || directionFilter
    ? 'По заданным фильтрам ничего не найдено.'
    : 'Записей пока нет — нажмите «Загрузить документ».';

  return (
    <>
      <Note>
        Реестр претензий и исков. Загрузите файл (Word, PDF или скан) — агент сам разберёт
        требования, проверит сроки, посчитает неустойку и подготовит проект ответа.
        Карточка обновляется в реальном времени.
      </Note>

      <RegistryToolbar>
        <RegistrySearchBox
          placeholder="Поиск по контрагенту…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <RegistryFilterSelect
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | ClaimType)}
          title="Фильтр по типу"
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={directionFilter}
          onChange={(e) => setDirectionFilter(e.target.value as '' | ClaimDirection)}
          title="Фильтр по направлению"
        >
          {DIRECTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          title="Фильтр по стадии"
        >
          {STAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          title="Сортировка"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <Button
          onClick={() => {
            setMode('upload');
            setOpen(true);
          }}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          Загрузить документ
        </Button>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <ClaimsBarRight>
          {search && (
            <span>
              По запросу «<strong>{search}</strong>»{' · '}
              <ClaimsResetButton
                type="button"
                onClick={() => setSearchInput('')}
              >
                сбросить
              </ClaimsResetButton>
            </span>
          )}
          <RegistryExportButton
            type="button"
            onClick={onExport}
            disabled={isExporting || total === 0}
            $disabled={isExporting || total === 0}
            title="Скачать текущую выборку в CSV для Excel"
          >
            {isExporting ? 'Готовим…' : 'Экспорт CSV'}
          </RegistryExportButton>
        </ClaimsBarRight>
      </RegistryBar>

      <ClaimsTable
        items={items}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        sort={sort}
        order={order}
        onSort={toggleSort}
        onRowClick={(id) => navigate(`/agents/statut/claims/${id}`)}
      />

      {total > PAGE_SIZE && (
        <RegistryPagination>
          <span>
            Стр. {page + 1} из {totalPages} · показано {items.length} из {total}
          </span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Назад
            </RegistryPageBtn>
            {pages.map((p, idx) => {
              const prev = idx > 0 ? pages[idx - 1] : -1;
              const showGap = idx > 0 && p - prev > 1;
              return (
                <Fragment key={p}>
                  {showGap && <RegistryPageBtn disabled>…</RegistryPageBtn>}
                  <RegistryPageBtn type="button" $active={p === page} onClick={() => setPage(p)}>
                    {p + 1}
                  </RegistryPageBtn>
                </Fragment>
              );
            })}
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Вперёд →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      <CreateClaimModal
        open={open}
        mode={mode}
        canWrite={can_write}
        onClose={() => setOpen(false)}
        onModeChange={setMode}
        onClaimCreated={(claimId) => {
          if (claimId) {
            navigate(`/agents/statut/claims/${claimId}`);
          }
        }}
      />
    </>
  );
}
